import Captcha from 'components/Captcha/Captcha';
import ErrorMessage from 'components/ErrorMessage';
import CareerSchema from 'components/Forms/Schema/CareerSchema';
import { isValidSchema } from 'utils/SchemaUtils';
import { useState } from 'react';
import Router from 'next/router';
import dynamic from 'next/dynamic';
import { phoneNumberWithDash } from 'utils/commonUtils';
import { DropdownField, InputField, TextAreaField } from './FormField';

const Loading = dynamic(() => import('components/Loading'));

const initialState = {
    lotName: '',
    position: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    captchaVerified: false,
    file: null,
};

const CareerForm = (props: any) => {
    const lotSetup = props?.additionalValues?.lotSetup;
    const generalInfo = props?.settings?.generalInfo;

    const [record, setRecord] = useState(initialState);
    const [submitError, setSubmitError] = useState('');
    const [isSaving, setSaving] = useState(false);

    const onChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if(name === 'phone')
            value = phoneNumberWithDash(value);

        const newRecord = {
            [name]: value,
        };

        if(name === 'lotName') {
            newRecord.position = '';
        }

        setRecord((item) => ({
            ...item,
            ...newRecord,
        }));
    };

    const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, files } = e.target;

        const currentFile = files ? files[0] : null;

        if (currentFile && currentFile.type !== "application/pdf") {
            alert("Only PDF files are allowed.");
            e.target.value = "";
            return;
        }

        const maxSize = 20 * 1024 * 1024; // 20 MB en bytes
        if (currentFile && currentFile?.size > maxSize) {
            alert("Maximum file size is 20 MB.");
            e.target.value = "";
            return;
        }

        setRecord((prev: any) => ({
            ...prev,
            [name]: files && files.length > 0 ? files[0] : value,
        }));
       
    };

    const { isValid } = isValidSchema(CareerSchema, record);

    const onSubmit = async () => {
        if (!isValid) return;
        setSaving(true);
        setSubmitError('');

        try {
            const contactName = record.name;
            const email = record.email ?? null;
            const toEmail = lotSetup?.find((c: any)=> c.lotName === record.lotName)?.emails?.replace(' ', '') || generalInfo.hrEmail;

            const formData = new FormData();
            formData.append('from', email || '');
            formData.append('to', toEmail);
            formData.append('subject', `Information from career page : ${email ? email : contactName}`);
            formData.append('isHtml', 'true');
            formData.append(
                'message',
                `<strong>Name: </strong><span>${contactName}</span><br/><strong>Phone number: </strong><span>${record.phone}</span><br/><strong>Message: </strong><span>${record.message}</span><br/><strong>Lot: </strong><span>${record.lotName}</span><br/><strong>Position to Apply: </strong><span>${record.position}</span>`
            );

            // adjunto el archivo
            if (record.file) {
                formData.append('file', record.file);
            }
            
            const response = await fetch('/api/mailWithAttachment', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();
            setSaving(false);

            if (result?.data) {           
                Router.push("/career/thankyou");
            } else {
                setSubmitError('error while send email');
            }
        } catch (error: any) {
            setSaving(false);
            setSubmitError(error?.message || error);
        }
    };

    const onValidateCaptcha = async (val: boolean) => {
        setRecord({
            ...record,
            captchaVerified: val,
        });
    };

    return (
        <div className="row">
            <div className="col-lg-12 col-sm-6 col-xs-12">
                <form
                    className=""
                    id="careerform"
                    data-parsley-validate="true"
                    action="#"
                >
                    <div className="form-group">
                        <label htmlFor="career-position">Lot *</label>
                        <DropdownField
                            formSchema={CareerSchema} 
                            record={record} 
                            onChange={onChange} 
                            className="form-control" 
                            name="lotName" >
                            <option value="">Select the Lot</option>
                            {lotSetup.filter((c: any)=> c.positions.length > 0).map((c: any)=> (
                                <option key={c.lotName} value={c.lotName}>{c.lotName}</option>
                            ))}
                        </DropdownField>
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-position">Position *</label>
                        <DropdownField
                            formSchema={CareerSchema} 
                            record={record} 
                            onChange={onChange} 
                            className="form-control" 
                            name="position" >
                            <option value="">Select the position</option>
                            {lotSetup?.find((c: any)=> c.lotName === record.lotName)?.positions?.split(',')?.map((position: string)=> (
                                <option key={position} value={position}>{position}</option>
                            ))}
                        </DropdownField>
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-name">Name *</label>
                        <InputField
                            type="text"
                            className="form-control"
                            id="career-name"
                            placeholder="your name"
                            name="name"
                            onChange={onChange} 
                            record={record} 
                            formSchema={CareerSchema}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-email">Email *</label>
                        <InputField
                            type="email"
                            className="form-control"
                            id="career-email"
                            placeholder="johndoe@email.com"
                            name="email"
                            onChange={onChange} 
                            record={record} 
                            formSchema={CareerSchema}                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-phone">Phone *</label>
                        <InputField
                            type="tel"
                            className="form-control"
                            id="career-phone"
                            placeholder="999-999-9999"
                            name="phone"
                            record={record}
                            onChange={onChange}
                            value={record.phone}
                            formSchema={CareerSchema}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-phone">Upload CV (PDF only, max 20 MB)</label>
                        <input
                            type="file"
                            className="form-control"
                            name="file"
                            onChange={onChangeFile}
                            accept=".pdf"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="career-message">Message *</label>
                        <TextAreaField
                            className="form-control"
                            id="career-message"
                            placeholder="write your message here"
                            name="message"
                            onChange={onChange}
                            record={record} 
                            formSchema={CareerSchema}
                            rows={4}
                        ></TextAreaField>
                    </div>
                    <div className="col-lg-12 col-md-12">
                        <Captcha
                            onValidateCaptcha={(val: boolean)=> onValidateCaptcha(val)}
                        />
                    </div>
                    <div className="col-lg-12 col-md-12">
                        {!isValid && (<div><ErrorMessage message="Please fill all the required fields!." /></div>)}
                        {!record.captchaVerified && (<div><ErrorMessage message="Please solve the captcha." /></div>)}
                        <button disabled={isSaving || !isValid || !record.captchaVerified} onClick={onSubmit} className="button red">
                            {isSaving ? <Loading text='Sending information...' /> : 'Send message'}
                        </button>
                        {submitError.length > 0 && (<div><ErrorMessage message={submitError} /></div>)}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CareerForm;
